import React, { useEffect, useState } from "react";
import { Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog } from "lucide-react";

// WMO weather codes -> icon + label
function decodeWmo(code) {
  if (code === 0) return { Icon: Sun, label: "Sereno" };
  if ([1, 2].includes(code)) return { Icon: Sun, label: "Poco nuvoloso" };
  if (code === 3) return { Icon: Cloud, label: "Nuvoloso" };
  if ([45, 48].includes(code)) return { Icon: CloudFog, label: "Nebbia" };
  if ([51, 53, 55, 56, 57].includes(code)) return { Icon: CloudRain, label: "Pioviggine" };
  if ([61, 63, 65, 80, 81, 82].includes(code)) return { Icon: CloudRain, label: "Pioggia" };
  if ([71, 73, 75, 77, 85, 86].includes(code)) return { Icon: CloudSnow, label: "Neve" };
  if ([95, 96, 99].includes(code)) return { Icon: CloudLightning, label: "Temporale" };
  return { Icon: Cloud, label: "Variabile" };
}

// Meteo attuale a Roma (Open-Meteo) + data/ora corrente in tz Europe/Rome.
async function fetchTodayWeather() {
  const lat = 41.8919, lon = 12.5113;
  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}`
    + `&current=temperature_2m,weather_code`
    + `&daily=temperature_2m_max,temperature_2m_min`
    + `&timezone=Europe%2FRome&forecast_days=1`;
  const r = await fetch(url);
  const d = await r.json();
  return {
    temp: d.current?.temperature_2m,
    code: d.current?.weather_code,
    tmin: (d.daily?.temperature_2m_min || [])[0],
    tmax: (d.daily?.temperature_2m_max || [])[0],
  };
}

const WEEKDAY = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

function formatNowInRome() {
  // Ottieni ora e data corrente di Roma indipendentemente dal fuso del client.
  const fmt = new Intl.DateTimeFormat("it-IT", {
    timeZone: "Europe/Rome",
    weekday: "short", day: "2-digit", month: "short",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(new Date()).map((p) => [p.type, p.value]));
  const weekday = (parts.weekday || "").replace(".", "");
  const day = parts.day; const month = (parts.month || "").replace(".", "");
  const hh = parts.hour; const mm = parts.minute;
  const short = `${day} ${month} ${hh}:${mm}`;
  const long = `${weekday.charAt(0).toUpperCase() + weekday.slice(1)} ${day} ${month} · ${hh}:${mm}`;
  return { short, long };
}

export default function WeatherMatch() {
  const [state, setState] = useState({ loading: true });
  const [now, setNow] = useState(() => formatNowInRome());

  useEffect(() => {
    fetchTodayWeather()
      .then((w) => setState({ loading: false, ...w }))
      .catch(() => setState({ loading: false, err: true }));
    // Aggiorna l'orologio ogni 30s per mantenere data/ora fresche
    const id = setInterval(() => setNow(formatNowInRome()), 30000);
    return () => clearInterval(id);
  }, []);

  if (state.loading) {
    return (
      <div className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-slate-900/85 backdrop-blur-md border border-white/25 text-white/80 text-xs" data-testid="weather-loading">
        <Cloud className="w-4 h-4" /> Meteo…
      </div>
    );
  }
  if (state.err || state.temp == null) return null;
  const { Icon, label } = decodeWmo(state.code);
  return (
    <div className="inline-flex items-center gap-1.5 md:gap-2.5 h-10 md:h-11 px-3 md:px-4 rounded-full bg-slate-900/85 backdrop-blur-md border border-white/25 shadow-xl text-white whitespace-nowrap" data-testid="weather-match">
      <Icon className="w-4 h-4 shrink-0" style={{ color: "#facc15" }} />
      <span className="text-[10px] md:text-[11px] uppercase tracking-widest text-white/70 font-semibold hidden sm:inline">Roma — {now.long}</span>
      <span className="text-[10px] uppercase tracking-widest text-white/70 font-semibold sm:hidden">{now.short}</span>
      <span className="display font-black text-sm tabular-nums">
        {Math.round(state.temp)}°
      </span>
      {state.tmin != null && state.tmax != null && (
        <span className="text-[11px] text-white/70 hidden md:inline">{Math.round(state.tmin)}° / {Math.round(state.tmax)}°</span>
      )}
      <span className="text-[10px] md:text-[11px] text-white/60 hidden sm:inline">· {label}</span>
    </div>
  );
}
